import PropTypes from 'prop-types'
import React, {
  useState,
  useMemo,
  useCallback,
  useReducer,
  createContext,
  useContext
} from 'react'

import { getSession, storeSession } from '../utils/session'

const LoginContext = createContext({})

const defaultState = {
  otpMethodSelected: '',
  errorMessage: '',
  phoneNumber: '',
  countdown: 0,
  resendCountdown: 0,
  isSubmitting: false,
  isSubmitError: true,
  otpBlockCountdown: 0,

  attemptCounter: 0,
  attemptOTPLimit: 0,
  // 4423 -> blocker login 24 jam
  // 4429 -> too many request 30 mins
  // 5500 -> internal server error -> otp service error
  otpErrorCode: null,
  otpMethodsAttempt: []
}

const loginReducer = (state, action) => {
  switch (action.key) {
    case 'SET_FIELD_VALUE':
      return {
        ...state,
        [action.keyState]: action.value
      }
    case 'SET_MULTIPLE_FIELDS':
      return {
        ...state,
        ...action.values
      }
    case 'RESET_OTP_VALUES':
      return {
        ...state,
        otpMethodSelected: '',
        countdown: 0,
        resendCountdown: 0,
        isSubmitError: false,
        attemptCounter: 0,
        attemptOTPLimit: 0,
        otpErrorCode: null
      }
    default:
      return state
  }
}

const otpMethodDisplay = {
  sms: 'SMS',
  wa: 'WhatsApp'
}

export const LoginProvider = ({ children }) => {
  const [loginState, loginDispatch] = useReducer(loginReducer, defaultState)
  const [isResendDisabled, setIsResendDisabled] = useState(true)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: ''
  })
  const [otpValue, setOTPValue] = useState('')

  const otpMethodText = useMemo(
    () => otpMethodDisplay[loginState.otpMethodSelected],
    [loginState.otpMethodSelected]
  )

  const handleSelectOTP = useCallback((otpMethod) => {
    loginDispatch({
      key: 'SET_FIELD_VALUE',
      keyState: 'otpMethodSelected',
      value: otpMethod
    })
  }, [])

  const handleOTPErrorStateChange = useCallback(
    ({
      isSubmitError,
      errorCode,
      countdown,
      otpAttempt,
      resendCountdown,
      otpAttemptLimit,
      otpBlockCountdown,
      errorMessage
    }) => {
      const updates = {
        isSubmitError,
        otpErrorCode: errorCode
      }

      if (countdown) {
        updates.countdown = countdown
      }
      if (otpAttempt) {
        updates.attemptCounter = otpAttempt
      }
      if (resendCountdown) {
        updates.resendCountdown = resendCountdown
        updates.otpBlockCountdown = 0
      }
      if (otpAttemptLimit) {
        updates.attemptOTPLimit = otpAttemptLimit
      }
      if (otpBlockCountdown) {
        updates.resendCountdown = 0
        updates.otpBlockCountdown = otpBlockCountdown
      }
      if (errorMessage) {
        updates.errorMessage = errorMessage
      }

      // Single dispatch with all updates for synchronous state change
      loginDispatch({
        key: 'SET_MULTIPLE_FIELDS',
        values: updates
      })
    },
    []
  )

  const handleResetOTPValues = useCallback(() => {
    loginDispatch({
      key: 'RESET_OTP_VALUES'
    })
  }, [])

  const handleOTPReqErrorChange = useCallback((_, method) => {
    const otpAttempt = getSession('otp-attm')
    if (!otpAttempt?.attempt) {
      storeSession('otp-attm', { attempt: [method] })
    } else {
      const attemptUpdated = [...otpAttempt.attempt, method]
      storeSession('otp-attm', { attempt: attemptUpdated })
    }
  }, [])

  const handleResetSnackbar = useCallback(
    () => setSnackbar({ open: false, message: '' }),
    []
  )

  const handleOpenSnackbar = useCallback(
    (message) => {
      setSnackbar({ open: true, message })
      setTimeout(() => {
        handleResetSnackbar()
      }, 3000)
    },
    [handleResetSnackbar]
  )

  const handleSetIsSubmitError = useCallback((newValue) => {
    loginDispatch({
      key: 'SET_FIELD_VALUE',
      keyState: 'isSubmitError',
      value: newValue
    })
  }, [])

  const values = useMemo(
    () => ({
      ...loginState,
      otpMethodText,
      handleSelectOTP,
      isResendDisabled,
      handleOTPErrorStateChange,
      handleOTPReqErrorChange,
      handleResetOTPValues,
      handleOpenSnackbar,
      handleResetSnackbar,
      snackbar,
      setIsResendDisabled,
      handleSetIsSubmitError,
      otpValue,
      setOTPValue
    }),
    [
      loginState,
      otpMethodText,
      handleSelectOTP,
      isResendDisabled,
      handleOTPErrorStateChange,
      handleOTPReqErrorChange,
      handleResetOTPValues,
      handleOpenSnackbar,
      handleResetSnackbar,
      snackbar,
      setIsResendDisabled,
      handleSetIsSubmitError,
      otpValue,
      setOTPValue
    ]
  )

  return (
    <LoginContext.Provider value={values}>{children}</LoginContext.Provider>
  )
}

LoginProvider.propTypes = {
  children: PropTypes.node
}

export const useLogin = () => {
  const context = useContext(LoginContext)
  if (context === undefined) {
    throw new Error('useLogin must be used within a LoginProvider')
  }
  return context
}
